import { ReactNode } from "react";
import { BrowserRouter, HashRouter, Navigate, Route, Routes } from "react-router-dom";

import { ThemeProvider } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";

import { AuthProvider } from "./config/context/authContext";
import { FirebaseProvider } from "./config/context/firebaseContext";
import { MainStateProvider } from "./config/context/mainStateContext";
import { ModalProvider } from "./config/context/modalContext";
import { NotificationProvider } from "./config/context/notificationContext";
import RequireConfig from "./config/middleware/RequireConfig";
import RequireStaff from "./config/middleware/RequireStaff";
import { ScrollToTop } from "./config/middleware/ScrollToTop";

import theme from "./config/Theme";

import ErrorNotFoundPage from "./pages/ErrorNotFoundPage";

import HomePage from "./pages/HomePage";
import BusTab from "./pages/HomePage/BusTab";
import MenuTab from "./pages/HomePage/MenuTab";
import RatingTab from "./pages/HomePage/RatingTab";
import SpottTab from "./pages/HomePage/SpottTab";

import AdminBannerPage from "./pages/Admin/AdminBannerPage";
import AdminDashboardPage from "./pages/Admin/AdminDashboardPage";
import AdminErrorsPage from "./pages/Admin/AdminErrorsPage";
import AdminFilePage from "./pages/Admin/AdminFilePage";
import AdminNotificationPage from "./pages/Admin/AdminNotificationPage";
import AdminRemoteSettingsPage from "./pages/Admin/AdminRemoteSettingsPage";
import AdminRequestPage from "./pages/Admin/AdminRequestPage";
import AdminTestPage from "./pages/Admin/AdminTestPage";
import AdminUserPage from "./pages/Admin/AdminUserPage";
import PostPage from "./pages/Admin/PostPage";
import ReportPage from "./pages/Admin/ReportPage";
import UserPage from "./pages/Admin/UserPage";

import AccountChatPage from "./pages/AccountChatPage";
import AccountChatsPage from "./pages/AccountChatsPage";
import AccountDeletePage from "./pages/AccountDeletePage";
import AccountPage from "./pages/AccountPage";
import AccountPostsPage from "./pages/AccountPostsPage";
import CafeteriaPage from "./pages/CafeteriaPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import SettingsPage from "./pages/SettingsPage";

import CodePage from "./pages/CodePage";
import ConfirmPage from "./pages/ConfirmPage";
import HelpPage from "./pages/HelpPage";

import DashboardTab from "./pages/CafeteriaPage/DashboardTab";
import CafeteriaMenuTab from "./pages/CafeteriaPage/MenuTab";
import OrderTab from "./pages/CafeteriaPage/OrderTab";
import SettingsTab from "./pages/CafeteriaPage/SettingsTab";

import CafeteriaSubTab from "./pages/HomePage/MenuTab/CafeteriaSubTab";
import ListingSubTab from "./pages/HomePage/MenuTab/ListingSubTab";
import OrderSubTab from "./pages/HomePage/MenuTab/OrderSubTab";

import AdminBusRulePage from "./pages/Admin/AdminBusRulePage";
import AdminBusSchedulePage from "./pages/Admin/AdminBusSchedulePage";
import { CafeteriaProvider } from "./pages/CafeteriaPage/context";
import { OnlyChildren } from "./types/common";

function NestedProviders({ children }: OnlyChildren) {
  const selectedRouter = (childrenProviders: ReactNode) => {
    return "cordova" in window ? (
      <HashRouter>{childrenProviders}</HashRouter>
    ) : (
      <BrowserRouter>{childrenProviders}</BrowserRouter>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      {selectedRouter(
        <ModalProvider>
          <NotificationProvider>
            <FirebaseProvider>
              <AuthProvider>
                <MainStateProvider>
                  <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <ScrollToTop children={children} />
                  </LocalizationProvider>
                </MainStateProvider>
              </AuthProvider>
            </FirebaseProvider>
          </NotificationProvider>
        </ModalProvider>
      )}
    </ThemeProvider>
  );
}

export const availablePaths = [
  "/",
  "/home",
  "/home/bus",
  "/home/menu",
  "/home/spott",
  "/home/rating",
  "/help",
  "/settings",
  "/cafeteria",
  "/account",
  "/account/posts",
  "/account/chats",
  "/account/chat/:chatId",
  "/account/delete",
  //
  "/menu/",
  //
  "/admin/banner",
  "/admin/dashboard",
  "/admin/user",
  "/admin/notification",
  "/admin/file",
  "/admin/remote",
  "/admin/request",
  "/admin/errors",
  "/admin/test",
  "/report/:reportId",
  "/post/:postId",
  "/user/:userId",
  //
  "/privacy",
];

export default function App() {
  return (
    <NestedProviders>
      <Routes>
        {/* Public Routes */}

        <Route path="/" element={<Navigate to={"/home/bus"} />} />

        <Route path="/home" element={<HomePage />}>
          <Route path="bus" element={<BusTab />} />
          <Route path="spott" element={<SpottTab />} />
          <Route path="rating" element={<RatingTab />} />

          {/* Client Side Menu */}
          <Route path="menu" element={<MenuTab />}>
            <Route path="" element={<ListingSubTab />} />
            <Route path="orders" element={<OrderSubTab />} />
            <Route path=":cafeteriaId" element={<CafeteriaSubTab />} />
          </Route>
        </Route>

        <Route path="/help" element={<HelpPage />} />
        <Route path="/settings" element={<SettingsPage />} />

        {/* Authenticated Routes */}

        {/* Partner Side Menu */}

        <Route
          path="/cafeteria"
          element={
            <CafeteriaProvider>
              <CafeteriaPage />
            </CafeteriaProvider>
          }
        >
          <Route path="dashboard" element={<DashboardTab />} />
          <Route path="menu" element={<CafeteriaMenuTab />} />
          <Route path="orders" element={<OrderTab />} />
          <Route path="settings" element={<SettingsTab />} />
        </Route>

        <Route path="/account" element={<AccountPage />} />
        <Route path="/account/posts" element={<AccountPostsPage />} />

        <Route
          path="/account/chats"
          element={
            <RequireConfig config={"enabled_chat"}>
              <AccountChatsPage />
            </RequireConfig>
          }
        />

        <Route
          path="/account/chat/:chatId"
          element={
            <RequireConfig config={"enabled_chat"}>
              <AccountChatPage />
            </RequireConfig>
          }
        />

        <Route path="/account/delete" element={<AccountDeletePage />} />

        {/* Admin Routes */}

        <Route
          path="/admin/dashboard"
          element={
            <RequireStaff>
              <AdminDashboardPage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/user"
          element={
            <RequireStaff>
              <AdminUserPage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/notification"
          element={
            <RequireStaff>
              <AdminNotificationPage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/file"
          element={
            <RequireStaff>
              <AdminFilePage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/remote"
          element={
            <RequireStaff>
              <AdminRemoteSettingsPage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/request"
          element={
            <RequireStaff>
              <AdminRequestPage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/banner"
          element={
            <RequireStaff>
              <AdminBannerPage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/bus/schedules"
          element={
            <RequireStaff>
              <AdminBusSchedulePage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/bus/rules"
          element={
            <RequireStaff>
              <AdminBusRulePage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/errors"
          element={
            <RequireStaff>
              <AdminErrorsPage />
            </RequireStaff>
          }
        />
        <Route
          path="/admin/test"
          element={
            <RequireStaff>
              <AdminTestPage />
            </RequireStaff>
          }
        />
        <Route
          path="/report/:reportId"
          element={
            <RequireStaff>
              <ReportPage />
            </RequireStaff>
          }
        />
        <Route
          path="/post/:postId"
          element={
            <RequireStaff>
              <PostPage />
            </RequireStaff>
          }
        />
        <Route
          path="/user/:userId"
          element={
            <RequireStaff>
              <UserPage />
            </RequireStaff>
          }
        />

        {/* Maintenance Routes */}

        <Route path="/code" element={<CodePage />} />
        <Route path="/confirm" element={<ConfirmPage />} />

        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        <Route path="/*" element={<ErrorNotFoundPage />} />
      </Routes>
    </NestedProviders>
  );
}
